<template>
  <div
    class="carousel-deck-card"
    :class="{ 'landscape': fallbackImageType === 'landscape' }"
    @click="goToCard"
  >
    <span class="card-image">
      <img
        :src="imageUrl"
        alt="Card image"
        :class="{ 'landscape': fallbackImageType === 'landscape' }"
      />
    </span>
    <span class="card-title">
      <span>
        <icon
          v-if="isLocked"
          class="lock-icon"
          name="lock-outline"
        ></icon>
      </span>
      <span>{{ card.title }}</span>
    </span>
    <span class="item-label">
      {{ itemLabel }}
    </span>
  </div>
</template>

<script>
import CardMixin from '@/mixins/card';
import Icon from '@components/singletons/icon';

export default {
  components: { Icon },
  mixins: [CardMixin],
  props: {
    card: {
      type: Object,
      required: true,
    },
    deck: {
      type: Object,
      required: true,
    },
    fallbackIconAttachmentId: {
      type: Number,
    },
    fallbackImageType: {
      type: String,
    },
  },
  computed: {
    imageAttachmentId() {
      return this.card.featuredImageId || this.fallbackIconAttachmentId || '';
    },
    imageAttachment() {
      return this.$store.getters['attachments/get'][this.imageAttachmentId] || {};
    },
    imageUrl() {
      return this.imageAttachment.url || '';
    },
    isTestSimulation() {
      if (this.card.assetType !== 'Category') {
        return false;
      }
      const category = this.$store.getters['categories/categories'][this.card.assetId];
      const categoryTypeId = category && category.categoryTypeId;
      const categoryType = this.$store.getters['categoryTypes/categoryTypes'].filter(
        (type) => type.id === categoryTypeId,
      )[0];

      return categoryType.name.includes('simulation');
    },
    assetType() {
      if (this.card.assetType === 'Category') {
        return 'category';
      }
      return '';
    },
    assetId() {
      return this.card.assetId;
    },
    isLocked() {
      if (!this.assetType) {
        return false;
      }
      return !this.$access(this.$store, this.assetType, this.assetId);
    },
    destinationDeck() {
      if (this.card.assetType === 'Deck') {
        return this.$store.getters['decks/get'][this.card.assetId];
      }
      return null;
    },
    category() {
      if (this.card.assetType === 'Category') {
        const category = this.$store.getters['categories/categories'][this.card.assetId];
        return category;
      }
      return null;
    },
    itemLabel() {
      let quantity = 0;
      let type = '';

      if (this.category) {
        const category = this.$store.getters['categories/categories'][this.card.assetId];
        if (category.descendants.length > 0) {
          quantity = category.descendants.length;
          if (quantity === 1) {
            type = 'category';
          } else {
            type = 'categories';
          }
        } else if (this.card.navigationDestination === 'terminology') {
          quantity = category.counts?.total || 0;
          if (quantity === 1) {
            type = 'term';
          } else {
            type = 'terms';
          }
        } else if (this.card.navigationDestination === 'flashcards') {
          quantity = category.counts?.total || 0;
          if (quantity === 1) {
            type = 'flashcard';
          } else {
            type = 'flashcards';
          }
        } else if (this.card.navigationDestination === 'wux_test_simulation') {
          quantity = category.counts?.total || 0;
          if (quantity === 1) {
            type = 'question';
          } else {
            type = 'questions';
          }
        }
      } else if (this.destinationDeck) {
        quantity = this.destinationDeck.cards.length;
        if (quantity === 1) {
          type = 'item';
        } else {
          type = 'items';
        }
      } else if (this.card.navigationDestination === 'attachment_categories') {
        quantity = this.getCategoryTypeQuantity(this.card.assetId);
        if (quantity === 1) {
          type = 'category';
        } else {
          type = 'categories';
        }
      }

      if (quantity > 0) {
        return `${quantity} ${type}`;
      }
      return '';
    },
  },
  methods: {
    goToCard() {
      this.$analytics.events.viewedCard(this.card, this.deck);
      this.openCard(this.card, this.deck);
    },
    getCategoryTypeQuantity(categoryTypeId) {
      const categories = this.$store.getters['categories/all'].filter(
        (category) => category.categoryTypeId === categoryTypeId,
      );
      return categories.length;
    },
  },
};
</script>

<style scoped lang="scss">
.carousel-deck-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #fff;
  max-width: rem-calc(170);
  min-width: rem-calc(170);
  padding-bottom: rem-calc(20);

  &:has(.landscape) {
    max-width: rem-calc(367);
    min-width: rem-calc(367);
  }

  cursor: pointer;

  .card-image {
    max-width: rem-calc(170);
    max-height: rem-calc(170);

    &:has(.landscape) {
      max-width: rem-calc(367);
      min-width: rem-calc(367);
      }

      img {
      border: 1px solid #D0D5DD;
      border-radius: 0.5rem;
      min-width: rem-calc(170);
      min-height: rem-calc(170);
      max-height: rem-calc(170);
      object-fit: cover;

      &.landscape {
        min-width: rem-calc(367);
        height: rem-calc(170);
      }
    }
  }

  .card-title {
    font-size: rem-calc(16);
    line-height: rem-calc(20);
    margin-top: rem-calc(10);
    font-weight: 600;
    color: #232323;
  }

  .lock-icon {
    transform: translateY(2px);
  }

  .item-label {
    font-size: rem-calc(12);
    font-weight: 400;
    line-height: rem-calc(18);
    margin-top: rem-calc(5);
    color: #697586;
  }
}
</style>
